import { apiGetGuessPositionShareDetail } from "@/api/order";
import type { ReactNode } from "react";
import { create } from "zustand";

type ShareModalBodySlot = ( params: { data: any | null } ) => ReactNode;
type ShareModalOpenOptions = {
    bodySlot?: ShareModalBodySlot;
    orderNo?: string;
    tradeNo?: string;
    includeGuessType?: boolean;
};

interface ShareModalState {
    isOpen: boolean;
    guessNo: string | null;
    data: any | null;
    loading: boolean;
    canShare: boolean; // 是否是可以分享
    bodySlot: ShareModalBodySlot | null;
    // 分享图片/详情弹窗 - 返回 canShare 和 data 供调用方判断
    openShareModal: (
        guessNo: string,
        guessType: number,
        options?: ShareModalOpenOptions,
    ) => Promise<{ canShare: boolean; data: any | null }>;
    closeShareModal: () => void;

    // 通用分享面板（三方分享）
    isOpenSharePanel: boolean;
    shareUrl: string;
    shareTitle: string;
    shareText: string;
    shareImage: string;
    openSharePanel: (
        params: { shareUrl?: string; shareTitle?: string; shareText?: string; shareImage?: string },
    ) => void;
    closeSharePanel: () => void;
    clearSharePanel: () => void;
}

export const useShareModal = create<ShareModalState>( ( set, get ) => ( {
    isOpen: false,
    guessNo: null,
    data: null,
    loading: false,
    canShare: false,
    bodySlot: null,
    openShareModal: async ( guessNo: string, guessType, options ) => {
        // 先获取数据，不立即打开弹窗
        set( { guessNo, loading: true, data: null, canShare: false, bodySlot: options?.bodySlot ?? null } );
        try {
            const includeGuessType = options?.includeGuessType ?? true;
            const [ isOk, res ] = await apiGetGuessPositionShareDetail( {
                pageNum: 1,
                pageSize: 10,
                guessNo,
                ...( includeGuessType ? { guessType: guessType as 1 | 2 } : {} ),
                orderNo: options?.orderNo,
                tradeNo: options?.tradeNo,
                source: "share",
            } );
            if ( isOk && res.data && res.data.positionList ) {
                const data = res.data.positionList.find( item => item.guessType === guessType );
                const canShare = true;
                set( { data, canShare } );

                // 只有 canShare 为 true 时才打开弹窗
                if ( canShare ) {
                    set( { isOpen: true } );
                }

                return { canShare, data };
            } else {
                set( { canShare: false, data: null } );
                return { canShare: false, data: null };
            }
        } catch ( error ) {
            set( { canShare: false, data: null } );
            return { canShare: false, data: null };
        } finally {
            set( { loading: false } );
        }
    },
    closeShareModal: () => set( { isOpen: false, guessNo: null, data: null, bodySlot: null } ),

    // 通用分享面板状态
    isOpenSharePanel: false,
    shareUrl: "",
    shareTitle: "",
    shareText: "",
    shareImage: "",
    openSharePanel: ( { shareUrl = "", shareTitle = "Share", shareText = "", shareImage = "" } ) => {
        set( {
            isOpenSharePanel: true,
            shareUrl,
            shareTitle,
            shareText,
            shareImage,
        } );
    },
    closeSharePanel: () => set( { isOpenSharePanel: false } ),
    clearSharePanel: () => set( { shareUrl: "", shareTitle: "", shareText: "", shareImage: "" } ),
} ) );
